import React, { useMemo, useState } from 'react';
import EmptyState from '../EmptyState';
import useIsMobile from './useIsMobile';
import { MONTHS_AR, fmt, projLabel, sortProjects, ST_LABELS, ST_COLORS } from './constants';

// Small badge showing the linked unit's status (مباع / محجوز / أفرغ …) on a deal row.
function UnitStatusBadge({ status, lang }) {
  if (!status) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const color = ST_COLORS[status] || '#94a3b8';
  const label = lang === 'ar' ? (ST_LABELS[status]?.ar || status) : (ST_LABELS[status]?.en || status);
  return (
    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.66rem', fontWeight: 800, background: color + '22', color, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// ── DealsTracker ──
// Dedicated tab for tracking ALL deals. Filter by project / month / quarter /
// closer / channel. Each row shows the closer, broker, channel, price,
// commissions, and a delete action. Desktop = table, mobile = stacked rows.
function DealsTracker({ deals, units, team, onDelete, onEdit, onLaunchDeal, t, lang }) {
  const isMobile = useIsMobile();
  const [projFilter, setProjFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [closerFilter, setCloserFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');

  const projects = useMemo(
    () => sortProjects([...new Set(units.map((u) => u.project))]),
    [units]
  );

  // Distinct unit statuses present among the deals (for the status filter).
  const statuses = useMemo(
    () => [...new Set(deals.map((d) => d.unit_status).filter(Boolean))],
    [deals]
  );

  const filtered = useMemo(() => {
    return deals.filter(
      (d) =>
        (!projFilter || d.project === projFilter) &&
        (!monthFilter || d.deal_month === monthFilter) &&
        (!statusFilter || d.unit_status === statusFilter) &&
        (!closerFilter || d.closer === closerFilter) &&
        (!channelFilter || d.channel === channelFilter)
    );
  }, [deals, projFilter, monthFilter, statusFilter, closerFilter, channelFilter]);

  // Totals over the filtered set so admins can see scope at a glance.
  const totals = useMemo(() => {
    const totalValue = filtered.reduce((a, d) => a + Number(d.actual_price || d.price || 0), 0);
    const totalTeamComm = filtered.reduce((a, d) => a + Number(d.mgr_amt || 0) + Number(d.closer_amt || 0), 0);
    const totalBrokerComm = filtered.reduce((a, d) => a + Number(d.broker_amt || 0), 0);
    return { count: filtered.length, totalValue, totalTeamComm, totalBrokerComm };
  }, [filtered]);

  const channelLabel = (ch) =>
    ch === 'direct' ? t('udDirect')
      : ch === 'broker' ? t('udBroker')
      : ch === 'mixed' ? t('udMixed')
      : ch === 'reservation' ? (lang === 'ar' ? 'حجز' : 'Reservation')
      : ch;

  const reset = () => {
    setProjFilter('');
    setMonthFilter('');
    setStatusFilter('');
    setCloserFilter('');
    setChannelFilter('');
  };

  const hasFilter = projFilter || monthFilter || statusFilter || closerFilter || channelFilter;

  return (
    <div>
      {/* ── Page header: title + primary CTA ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontSize: '0.62rem',
              color: 'var(--text-secondary)',
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              marginBottom: 2,
            }}
          >
            {lang === 'ar' ? 'متابعة الصفقات' : 'Deals Tracking'}
          </div>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>
            {totals.count} {lang === 'ar' ? (totals.count === 1 ? 'صفقة' : 'صفقة') : (totals.count === 1 ? 'deal' : 'deals')}
            {hasFilter && (
              <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 600, marginInlineStart: 8 }}>
                {lang === 'ar' ? '(مفلترة)' : '(filtered)'}
              </span>
            )}
          </div>
        </div>
        {onLaunchDeal && (
          <button
            onClick={onLaunchDeal}
            className="btn btn-primary"
            style={{
              padding: '9px 16px',
              fontSize: '0.82rem',
              fontWeight: 800,
              whiteSpace: 'nowrap',
              flex: isMobile ? '1 1 100%' : '0 0 auto',
              minHeight: 40,
            }}
          >
            {lang === 'ar' ? '+ تسجيل صفقة' : '+ Register Deal'}
          </button>
        )}
      </div>

      {/* ── KPI strip ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <SummaryChip label={lang === 'ar' ? 'إجمالي الصفقات' : 'Total Deals'} value={totals.count} color="var(--accent)" />
        <SummaryChip label={lang === 'ar' ? 'قيمة المبيعات' : 'Sales Value'} value={fmt(totals.totalValue)} suffix="SAR" color="#c9a84c" />
        <SummaryChip label={lang === 'ar' ? 'عمولات الفريق' : 'Team Commissions'} value={fmt(totals.totalTeamComm)} suffix="SAR" color="#3b82f6" />
        <SummaryChip label={lang === 'ar' ? 'عمولات الوسطاء' : 'Broker Commissions'} value={fmt(totals.totalBrokerComm)} suffix="SAR" color="#f59e0b" />
      </div>

      {/* ── Filter row: labeled selects + active-filter chips ── */}
      <div
        style={{
          padding: '10px 12px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, minmax(0, 1fr))',
            gap: 8,
          }}
        >
          <LabeledSelect
            label={lang === 'ar' ? 'المشروع' : 'Project'}
            value={projFilter}
            onChange={setProjFilter}
          >
            <option value="">{lang === 'ar' ? 'الكل' : 'All'}</option>
            {projects.map((p) => (
              <option key={p} value={p}>{projLabel(p, t)}</option>
            ))}
          </LabeledSelect>
          <LabeledSelect
            label={lang === 'ar' ? 'الشهر' : 'Month'}
            value={monthFilter}
            onChange={setMonthFilter}
          >
            <option value="">{lang === 'ar' ? 'الكل' : 'All'}</option>
            {MONTHS_AR.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </LabeledSelect>
          <LabeledSelect
            label={lang === 'ar' ? 'الحالة' : 'Status'}
            value={statusFilter}
            onChange={setStatusFilter}
          >
            <option value="">{lang === 'ar' ? 'الكل' : 'All'}</option>
            {statuses.map((s) => (
              <option key={s} value={s}>{lang === 'ar' ? (ST_LABELS[s]?.ar || s) : (ST_LABELS[s]?.en || s)}</option>
            ))}
          </LabeledSelect>
          <LabeledSelect
            label={lang === 'ar' ? 'المنفّذ' : 'Closer'}
            value={closerFilter}
            onChange={setCloserFilter}
          >
            <option value="">{lang === 'ar' ? 'الكل' : 'All'}</option>
            {team.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </LabeledSelect>
          <LabeledSelect
            label={lang === 'ar' ? 'القناة' : 'Channel'}
            value={channelFilter}
            onChange={setChannelFilter}
          >
            <option value="">{lang === 'ar' ? 'الكل' : 'All'}</option>
            <option value="direct">{t('udDirect')}</option>
            <option value="broker">{t('udBroker')}</option>
            <option value="mixed">{t('udMixed')}</option>
          </LabeledSelect>
        </div>

        {hasFilter && (
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              alignItems: 'center',
              marginTop: 10,
              paddingTop: 10,
              borderTop: '1px dashed var(--border)',
            }}
          >
            <span style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>
              {lang === 'ar' ? 'النشط:' : 'Active:'}
            </span>
            {projFilter && (
              <FilterChip
                label={`${lang === 'ar' ? 'المشروع' : 'Project'}: ${projLabel(projFilter, t)}`}
                onRemove={() => setProjFilter('')}
              />
            )}
            {monthFilter && (
              <FilterChip
                label={`${lang === 'ar' ? 'الشهر' : 'Month'}: ${monthFilter}`}
                onRemove={() => setMonthFilter('')}
              />
            )}
            {statusFilter && (
              <FilterChip
                label={`${lang === 'ar' ? 'الحالة' : 'Status'}: ${lang === 'ar' ? (ST_LABELS[statusFilter]?.ar || statusFilter) : (ST_LABELS[statusFilter]?.en || statusFilter)}`}
                onRemove={() => setStatusFilter('')}
              />
            )}
            {closerFilter && (
              <FilterChip
                label={`${lang === 'ar' ? 'المنفّذ' : 'Closer'}: ${closerFilter}`}
                onRemove={() => setCloserFilter('')}
              />
            )}
            {channelFilter && (
              <FilterChip
                label={`${lang === 'ar' ? 'القناة' : 'Channel'}: ${channelLabel(channelFilter)}`}
                onRemove={() => setChannelFilter('')}
              />
            )}
            <button
              onClick={reset}
              style={{
                padding: '5px 10px',
                fontSize: '0.7rem',
                fontWeight: 700,
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 999,
                cursor: 'pointer',
                marginInlineStart: 'auto',
              }}
            >
              {lang === 'ar' ? 'مسح الكل' : 'Clear all'}
            </button>
          </div>
        )}
      </div>

      {/* ── List ── */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={"\uD83D\uDCDD"}
          title={lang === 'ar' ? 'لا توجد صفقات' : 'No deals'}
          subtitle={lang === 'ar' ? 'لم يتم تسجيل أي صفقة بهذه الفلاتر' : 'No deals match these filters'}
        />
      ) : isMobile ? (
        <MobileDeals rows={filtered} channelLabel={channelLabel} onDelete={onDelete} onEdit={onEdit} t={t} lang={lang} />
      ) : (
        <DesktopDeals rows={filtered} channelLabel={channelLabel} onDelete={onDelete} onEdit={onEdit} t={t} lang={lang} />
      )}
    </div>
  );
}

function DesktopDeals({ rows, channelLabel, onDelete, onEdit, t, lang }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="data-table-wrapper" style={{ maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' }}>
        <table className="data-table" style={{ fontSize: '0.78rem' }}>
          <thead>
            <tr>
              <th style={stickyTh}>{t('udProject')}</th>
              <th style={stickyTh}>{t('udUnit')}</th>
              <th style={stickyTh}>{lang === 'ar' ? 'الحالة' : 'Status'}</th>
              <th style={stickyTh}>{lang === 'ar' ? 'القناة' : 'Channel'}</th>
              <th style={stickyTh}>{t('udCloser')}</th>
              <th style={stickyTh}>{t('udBrokerName')}</th>
              <th style={{ ...stickyTh, textAlign: 'left' }}>{t('udPrice')}</th>
              <th style={{ ...stickyTh, textAlign: 'left' }}>{lang === 'ar' ? 'عمولة الفريق' : 'Team Comm.'}</th>
              <th style={{ ...stickyTh, textAlign: 'left' }}>{lang === 'ar' ? 'عمولة الوسيط' : 'Broker Comm.'}</th>
              <th style={stickyTh}>{t('udMonth')}</th>
              <th style={stickyTh}>{t('udQuarter')}</th>
              <th style={{ ...stickyTh, textAlign: 'center' }}>{lang === 'ar' ? 'تاريخ' : 'Date'}</th>
              <th style={{ ...stickyTh, textAlign: 'center', width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const teamComm = Number(d.mgr_amt || 0) + Number(d.closer_amt || 0);
              const brokerComm = Number(d.broker_amt || 0);
              return (
                <tr key={d.id}>
                  <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{projLabel(d.project, t)}</td>
                  <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{d.unit_code}</td>
                  <td><UnitStatusBadge status={d.unit_status} lang={lang} /></td>
                  <td>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 10,
                        fontSize: '0.66rem',
                        fontWeight: 700,
                        background:
                          d.channel === 'direct' ? '#22c55e18'
                          : d.channel === 'broker' ? '#f59e0b18'
                          : d.channel === 'reservation' ? '#94a3b818'
                          : '#6366f118',
                        color:
                          d.channel === 'direct' ? '#22c55e'
                          : d.channel === 'broker' ? '#f59e0b'
                          : d.channel === 'reservation' ? '#94a3b8'
                          : '#6366f1',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {channelLabel(d.channel)}
                    </span>
                  </td>
                  <td style={{ color: 'var(--accent)', fontWeight: 700 }}>{d.closer || '—'}</td>
                  <td style={{ color: d.broker_name ? '#f59e0b' : 'var(--text-secondary)', fontSize: '0.92rem', fontWeight: 800 }}>
                    {d.broker_name ? `${d.broker_name} (${d.broker_pct}%)` : '—'}
                  </td>
                  <td style={{ direction: 'ltr', fontWeight: 800, textAlign: 'left', color: '#c9a84c' }}>
                    {fmt(d.actual_price || d.price)}
                  </td>
                  <td style={{ direction: 'ltr', textAlign: 'left' }}>{fmt(teamComm)}</td>
                  <td style={{ direction: 'ltr', textAlign: 'left', color: brokerComm > 0 ? '#f59e0b' : 'var(--text-secondary)' }}>
                    {brokerComm > 0 ? fmt(brokerComm) : '—'}
                  </td>
                  <td>{d.deal_month}</td>
                  <td>{d.quarter}</td>
                  <td style={{ textAlign: 'center', direction: 'ltr', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                    {d.deal_date ? new Date(d.deal_date).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      {onEdit && (
                        <button
                          onClick={() => onEdit(d)}
                          style={{
                            padding: '8px 14px',
                            fontSize: '0.85rem',
                            fontWeight: 800,
                            background: 'rgba(99,102,241,0.12)',
                            border: '1.5px solid var(--accent)',
                            color: 'var(--accent)',
                            borderRadius: 6,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {lang === 'ar' ? 'تحديث الصفقة' : 'Update Deal'}
                        </button>
                      )}
                      {onDelete && (
                      <button
                        onClick={() => onDelete(d.id)}
                        style={{
                          padding: '8px 14px',
                          fontSize: '0.85rem',
                          fontWeight: 800,
                          background: 'rgba(239,68,68,0.08)',
                          border: '1.5px solid rgba(239,68,68,0.4)',
                          color: '#ef4444',
                          borderRadius: 6,
                          cursor: 'pointer',
                        }}
                      >
                        {lang === 'ar' ? 'حذف' : 'Delete'}
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MobileDeals({ rows, channelLabel, onDelete, onEdit, t, lang }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {rows.map((d, i) => {
        const teamComm = Number(d.mgr_amt || 0) + Number(d.closer_amt || 0);
        const brokerComm = Number(d.broker_amt || 0);
        const channelColor =
          d.channel === 'direct' ? '#22c55e'
          : d.channel === 'broker' ? '#f59e0b'
          : d.channel === 'reservation' ? '#94a3b8'
          : '#6366f1';
        return (
          <div
            key={d.id}
            style={{
              padding: '10px 12px',
              borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 800 }}>
                  {projLabel(d.project, t)} — {d.unit_code}
                </div>
                <div style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', marginTop: 1 }}>
                  {d.deal_month} · {d.quarter}
                </div>
                <div style={{ marginTop: 4 }}><UnitStatusBadge status={d.unit_status} lang={lang} /></div>
              </div>
              <span
                style={{
                  padding: '3px 9px',
                  borderRadius: 12,
                  fontSize: '0.66rem',
                  fontWeight: 700,
                  background: channelColor + '18',
                  color: channelColor,
                  whiteSpace: 'nowrap',
                }}
              >
                {channelLabel(d.channel)}
              </span>
            </div>

            <div style={{ display: 'flex', gap: 12, fontSize: '0.74rem', flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ direction: 'ltr', fontWeight: 800, color: '#c9a84c' }}>
                {fmt(d.actual_price || d.price)} SAR
              </span>
              {d.closer && <span style={{ color: 'var(--accent)', fontWeight: 700 }}>· {d.closer}</span>}
              {d.broker_name && (
                <span style={{ color: '#f59e0b', fontWeight: 800, fontSize: '0.92rem' }}>
                  · {d.broker_name} ({d.broker_pct}%)
                </span>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
              <span>{lang === 'ar' ? 'عمولة الفريق' : 'Team'}: <b style={{ direction: 'ltr' }}>{fmt(teamComm)}</b></span>
              {brokerComm > 0 && (
                <span style={{ color: '#f59e0b' }}>{lang === 'ar' ? 'وسيط' : 'Broker'}: <b style={{ direction: 'ltr' }}>{fmt(brokerComm)}</b></span>
              )}
              <div style={{ display: 'flex', gap: 6, marginInlineStart: 'auto' }}>
                {onEdit && (
                  <button
                    onClick={() => onEdit(d)}
                    style={{
                      background: 'rgba(99,102,241,0.12)',
                      border: '1.5px solid var(--accent)',
                      color: 'var(--accent)',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: 800,
                      padding: '9px 14px',
                      borderRadius: 6,
                      minHeight: 38,
                    }}
                  >
                    {lang === 'ar' ? 'تحديث الصفقة' : 'Update Deal'}
                  </button>
                )}
                {onDelete && (
                <button
                  onClick={() => onDelete(d.id)}
                  style={{
                    background: 'rgba(239,68,68,0.08)',
                    border: '1.5px solid rgba(239,68,68,0.4)',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 800,
                    padding: '9px 14px',
                    borderRadius: 6,
                    minHeight: 38,
                  }}
                >
                  {lang === 'ar' ? 'حذف' : 'Delete'}
                </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LabeledSelect({ label, value, onChange, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span
        style={{
          fontSize: '0.62rem',
          color: 'var(--text-secondary)',
          fontWeight: 700,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '7px 10px',
          borderRadius: 6,
          border: '1px solid var(--border)',
          fontSize: '0.78rem',
          background: 'var(--bg-card)',
          color: 'var(--text-primary)',
        }}
      >
        {children}
      </select>
    </label>
  );
}

function FilterChip({ label, onRemove }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 4px 4px 10px',
        background: 'rgba(99,102,241,0.10)',
        border: '1px solid rgba(99,102,241,0.30)',
        color: 'var(--accent)',
        borderRadius: 999,
        fontSize: '0.72rem',
        fontWeight: 700,
      }}
    >
      {label}
      <button
        onClick={onRemove}
        aria-label="Remove filter"
        style={{
          width: 18,
          height: 18,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 999,
          border: 'none',
          background: 'rgba(99,102,241,0.18)',
          color: 'var(--accent)',
          cursor: 'pointer',
          fontSize: '0.8rem',
          lineHeight: 1,
          padding: 0,
        }}
      >
        {'\u00D7'}
      </button>
    </span>
  );
}

function SummaryChip({ label, value, suffix, color }) {
  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderInlineStart: '3px solid ' + color,
        borderRadius: 8,
      }}
    >
      <div style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', marginBottom: 4, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, direction: 'ltr' }}>
        <span style={{ fontSize: '1.25rem', fontWeight: 800, color, lineHeight: 1.1 }}>{value}</span>
        {suffix && <span style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', fontWeight: 700 }}>{suffix}</span>}
      </div>
    </div>
  );
}

const stickyTh = { position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1, whiteSpace: 'nowrap' };

export default DealsTracker;
